// Shared preference-picker primitives.
//
// These are extracted from the onboarding step components in NomLoop.jsx so
// they can be reused inside a Settings modal without re-running the
// onboarding step machine. Onboarding screens compose these via OnboardWrap;
// the Settings modal composes them inside a panel layout. Either way, the
// pickers themselves are stateless presentational components — they take
// the current value and an onChange callback and render the chosen control.
//
// If you need to add a new preference, add a picker here and re-use it
// in both surfaces. Do NOT inline a picker in NomLoop.jsx for a new field —
// that's how onboarding and settings drifted apart in the first place.

import { C } from "../../theme.js";
import { ALLERGENS, DIETS, CUISINES, DISLIKE_OPTIONS, CUISINE_DIET_NOTES } from "../../constants.js";
import { Chip } from "../Chip.jsx";
import { PantryReveal } from "../PantryReveal.jsx";

/* ───────────── PeoplePicker ───────────── */
export function PeoplePicker({ value, onChange, max = 8 }) {
  return (
    <div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            onClick={() => onChange(n)}
            aria-pressed={value === n}
            style={{
              width: 56, height: 56, borderRadius: 14,
              border: `2px solid ${value === n ? C.green : C.border}`,
              background: value === n ? C.greenPale : C.white,
              fontWeight: 700, fontSize: 18,
              color: value === n ? C.green : C.charcoal, cursor: "pointer",
              transition: "all .18s", boxShadow: value === n ? C.sh : "none",
            }}
          >
            {n}
          </button>
        ))}
      </div>
      <p style={{ color: C.grey, fontSize: 14, marginTop: 12 }}>
        {value === 1
          ? "Just yourself — we'll keep portions practical."
          : `${value} people — portions and shopping will scale accordingly.`}
      </p>
    </div>
  );
}

/* ───────────── DietPicker ───────────── */
export function DietPicker({ value, onChange }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {DIETS.map((d) => (
        <button
          key={d.id}
          onClick={() => onChange(d.id)}
          aria-pressed={value === d.id}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "16px 18px", borderRadius: 14,
            border: `1.5px solid ${value === d.id ? C.gold : C.border}`,
            background: value === d.id ? "#FFFBEA" : C.white,
            cursor: "pointer", transition: "all .18s",
            boxShadow: value === d.id ? C.sh : "none",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 22 }}>{d.emoji}</span>
            <span style={{ fontWeight: 700, fontSize: 16, color: C.charcoal }}>{d.label}</span>
          </div>
          <span style={{ fontSize: 13, color: C.grey, textAlign: "right" }}>{d.desc}</span>
        </button>
      ))}
    </div>
  );
}

/* ───────────── AllergyPicker ───────────── */
export function AllergyPicker({ value = [], onChange }) {
  const toggle = (a) =>
    onChange(value.includes(a) ? value.filter((x) => x !== a) : [...value, a]);
  return (
    <div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {ALLERGENS.map((a) => (
          <Chip key={a} label={a} selected={value.includes(a)} onClick={() => toggle(a)} />
        ))}
      </div>
      {/* Reserve space for the tip banner so the chip grid above never reflows
          when the user toggles allergens. Without minHeight here, the layout
          shifts on first/last selection and rapid clicks land on the wrong chip. */}
      <div style={{ minHeight: 60, marginTop: 16 }}>
        {value.length > 0 && (
          <div
            style={{
              padding: "12px 16px", borderRadius: 12,
              background: C.tealPale, color: C.teal, fontSize: 14, fontWeight: 600,
            }}
          >
            ✓ Meals containing {value.join(", ")} will be hidden.
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────────── DislikesPicker ───────────── */
export function DislikesPicker({ value = [], onChange }) {
  return (
    <div>
      <PantryReveal
        selected={value}
        options={DISLIKE_OPTIONS}
        onChange={onChange}
        placeholder="Search ingredients… (e.g. mushrooms)"
      />
      {value.length > 0 && (
        <div
          style={{
            marginTop: 16, padding: "12px 16px", borderRadius: 12,
            background: C.sand, color: C.grey, fontSize: 13, lineHeight: 1.5,
          }}
        >
          Meals featuring {value.slice(0, 3).join(", ")}
          {value.length > 3 ? ` and ${value.length - 3} more` : ""} will be hidden from your plan.
        </div>
      )}
    </div>
  );
}

/* ───────────── CuisinePicker ─────────────
   Onboarding shows pair-hint chips; Settings shows the picker without hints.
   The hints prop is optional — when omitted, the picker behaves identically
   to onboarding's no-pair-hints state. */
export function CuisinePicker({ value = [], onChange, max = 2, pairHints = {} }) {
  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((x) => x !== id));
    else if (value.length < max) onChange([...value, id]);
  };
  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {CUISINES.map((c) => {
          const isSelected = value.includes(c.id);
          const isDisabled = !isSelected && value.length >= max;
          const hintLabel = !isSelected && value.length === 1 ? pairHints[c.id] : null;
          const chipStyle =
            hintLabel === "Perfect match"
              ? { bg: C.greenPale, fg: C.green, icon: "✨" }
              : hintLabel === "Classic combo" || hintLabel === "Classic pairing"
              ? { bg: C.greenPale, fg: C.green, icon: "★" }
              : hintLabel === "Fresh combo"
              ? { bg: C.tealPale, fg: C.teal, icon: "🌿" }
              : hintLabel === "Bold combo"
              ? { bg: C.goldPale, fg: C.amber, icon: "🌶️" }
              : hintLabel === "Adventurous pick"
              ? { bg: C.sand, fg: C.grey, icon: "🧭" }
              : null;
          return (
            <button
              key={c.id}
              onClick={() => !isDisabled && toggle(c.id)}
              disabled={isDisabled}
              aria-pressed={isSelected}
              style={{
                display: "flex", alignItems: "center", gap: 14,
                padding: "15px 18px", borderRadius: 16,
                cursor: isDisabled ? "not-allowed" : "pointer",
                border: `2px solid ${isSelected ? C.green : C.border}`,
                background: isSelected ? C.greenPale : C.white,
                opacity: isDisabled ? 0.4 : 1, transition: "all .18s",
                boxShadow: isSelected ? C.sh : "none",
              }}
            >
              <span style={{ fontSize: 32 }}>{c.emoji}</span>
              <div style={{ textAlign: "left", flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: C.charcoal }}>{c.label}</div>
                  {chipStyle && (
                    <span
                      style={{
                        fontSize: 11, fontWeight: 700,
                        padding: "2px 8px", borderRadius: 99,
                        background: chipStyle.bg, color: chipStyle.fg,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {chipStyle.icon} {hintLabel}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: C.grey }}>{c.desc}</div>
              </div>
              {isSelected && (
                <div
                  style={{
                    marginLeft: "auto", width: 22, height: 22, borderRadius: "50%",
                    background: C.green, display: "flex", alignItems: "center",
                    justifyContent: "center", color: C.white, fontSize: 12, fontWeight: 700,
                  }}
                >
                  ✓
                </div>
              )}
            </button>
          );
        })}
      </div>
      {value.length === max && (
        <p style={{ marginTop: 12, fontSize: 13, color: C.grey }}>
          Maximum {max} cuisines selected. This helps the waste-reduction algorithm work better.
        </p>
      )}
    </div>
  );
}

/* ───────────── MealCountPicker ───────────── */
export function MealCountPicker({ value, onChange, max = 7 }) {
  return (
    <div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            onClick={() => onChange(n)}
            aria-pressed={value === n}
            style={{
              width: 56, height: 56, borderRadius: 14,
              border: `2px solid ${value === n ? C.green : C.border}`,
              background: value === n ? C.greenPale : C.white,
              fontWeight: 700, fontSize: 18,
              color: value === n ? C.green : C.charcoal, cursor: "pointer",
              transition: "all .18s", boxShadow: value === n ? C.sh : "none",
            }}
          >
            {n}
          </button>
        ))}
      </div>
      <p style={{ color: C.grey, fontSize: 14, marginTop: 14, lineHeight: 1.5 }}>
        {value <= 3
          ? "Great for planning a few special dinners around other commitments."
          : value <= 5
          ? "A balanced week with room for leftovers and takeaways."
          : "Full weekly planner — maximum waste reduction across all meals."}
      </p>
    </div>
  );
}

/* ───────────── BudgetPicker ───────────── */
export function BudgetPicker({ value, onChange, people = 2, mealTarget = 5 }) {
  return (
    <div>
      <div
        style={{
          background: C.white, borderRadius: 18, padding: "24px 20px",
          boxShadow: C.sh, marginBottom: 20,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
          <span style={{ color: C.grey, fontSize: 14 }}>Weekly grocery budget</span>
          <span style={{ fontFamily: "Lora,serif", fontWeight: 700, fontSize: 28, color: C.green }}>
            £{value}
          </span>
        </div>
        <input
          type="range"
          min={25}
          max={200}
          step={5}
          value={value}
          onChange={(e) => onChange(+e.target.value)}
          style={{ width: "100%", accentColor: C.green }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", color: C.greyLight, fontSize: 12, marginTop: 6 }}>
          <span>£25</span><span>£200</span>
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {[40, 55, 70, 90, 120].map((n) => (
          <button
            key={n}
            onClick={() => onChange(n)}
            aria-pressed={value === n}
            style={{
              padding: "9px 16px", borderRadius: 99,
              border: `1.5px solid ${value === n ? C.green : C.border}`,
              background: value === n ? C.greenPale : C.white,
              fontWeight: 600, fontSize: 14,
              color: value === n ? C.green : C.charcoal, cursor: "pointer",
            }}
          >
            £{n}
          </button>
        ))}
      </div>
      <div
        style={{
          marginTop: 20, padding: "14px 16px", borderRadius: 12,
          background: C.greenPale, fontSize: 14, color: C.green, fontWeight: 600,
        }}
      >
        💡 Most households adjust this based on retailer, recipe choices, and how many pantry staples they already own.
      </div>
    </div>
  );
}

// Re-export useful constants so the SettingsModal doesn't need to import them
// separately. Keeps the import surface tight.
export { ALLERGENS, DIETS, CUISINES, DISLIKE_OPTIONS, CUISINE_DIET_NOTES };
